import * as React from 'react';
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/ui/lib/utils';

const buttonVariants = cva(
  'tw:relative tw:box-border tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:gap-2.5 tw:rounded-md tw:border-2 tw:text-sm tw:leading-none tw:font-semibold tw:tracking-[0.04em] tw:whitespace-nowrap tw:transition-[color,background-color,border-color,box-shadow,opacity] tw:outline-none tw:enabled:cursor-pointer tw:focus-visible:ring-3 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-2 tw:focus-visible:ring-offset-background tw:disabled:pointer-events-none tw:disabled:text-muted-foreground tw:aria-invalid:border-destructive tw:aria-invalid:ring-destructive/20 tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-6',
  {
    variants: {
      variant: {
        default:
          'tw:border-primary tw:bg-primary tw:text-primary-foreground tw:hover:border-primary-hover tw:hover:bg-primary-hover tw:disabled:border-form-border tw:disabled:bg-form-border',
        outline:
          'tw:border-primary tw:bg-background tw:text-primary tw:hover:opacity-70 tw:disabled:border-form-border tw:disabled:bg-muted',
        secondary:
          'tw:border-muted tw:bg-background tw:text-primary tw:hover:opacity-70 tw:disabled:border-muted tw:disabled:bg-muted',
        link: 'tw:border-transparent tw:bg-transparent tw:text-primary tw:underline-offset-4 tw:hover:opacity-70 tw:hover:underline',
      },
      size: {
        default: 'tw:h-12 tw:min-w-30 tw:px-4 tw:py-1',
        sm: 'tw:h-11 tw:min-w-18 tw:px-3 tw:py-2.5',
        lg: 'tw:h-15 tw:min-w-55 tw:px-4 tw:py-1 tw:text-base/none',
      },
      // 幅は利用側のクラスでなく fill で制御する。false（既定）は内容にフィットし、
      // true は親コンテナいっぱいに広がる。ただし size ごとの最小幅（min-w）は
      // fill でも下回らない。最小幅より狭い親に置くのはレイアウト側の誤り
      //（あえてはみ出して可視化する）。
      fill: {
        true: 'tw:w-full',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        size: 'default',
        class: 'tw:text-base/none',
      },
      // data-icon="start" / "end" はラベルに対するアイコンの位置を宣言する。
      // link 以外では端に固定してラベルをボタン中央に保ち（Figma 準拠）、
      // link ではテキストと同じ流れのまま前後だけを入れ替える。
      // オフセットは Figma の外縁からの距離 − border-2 の 2px（left/right は padding box 基準のため）。
      // 端固定のアイコンは absolute でレイアウト外のため幅に算入されない。
      // link 以外で data-icon を使う時は fill か明示幅と必ず併用すること（内容フィット幅ではラベルと重なる）。
      {
        variant: ['default', 'outline', 'secondary'],
        size: ['sm', 'default', 'lg'],
        class:
          'tw:[&_[data-icon]]:absolute tw:[&_[data-icon]]:top-1/2 tw:[&_[data-icon]]:-translate-y-1/2',
      },
      {
        variant: ['default', 'outline', 'secondary'],
        size: 'sm',
        class:
          'tw:[&_[data-icon=start]]:left-2.5 tw:[&_[data-icon=end]]:right-2.5',
      },
      {
        variant: ['default', 'outline', 'secondary'],
        size: 'default',
        class:
          'tw:[&_[data-icon=start]]:left-3.5 tw:[&_[data-icon=end]]:right-3.5',
      },
      {
        variant: ['default', 'outline', 'secondary'],
        size: 'lg',
        class:
          'tw:[&_[data-icon=start]]:left-3 tw:[&_[data-icon=end]]:right-2.5',
      },
      {
        variant: 'link',
        size: ['sm', 'default', 'lg'],
        class:
          'tw:h-auto tw:min-h-11 tw:min-w-11 tw:gap-0.5 tw:border-0 tw:px-2 tw:py-2.5 tw:text-base/none tw:[&_[data-icon=start]]:order-first tw:[&_[data-icon=end]]:order-last',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fill: false,
    },
  },
);

type ButtonProps = ButtonPrimitive.Props & VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<HTMLElement, ButtonProps>(
  ({ className, variant, size, fill, ...props }, ref) => (
    <ButtonPrimitive
      ref={ref}
      data-slot="button"
      className={
        typeof className === 'function'
          ? (state: ButtonPrimitive.State) =>
              cn(buttonVariants({ variant, size, fill }), className(state))
          : cn(buttonVariants({ variant, size, fill }), className)
      }
      {...props}
    />
  ),
);

Button.displayName = 'Button';

export { Button, buttonVariants };
